import React from 'react';

const ImageFileInfoCard = ({
  selectedFile,
  isBatchMode,
  metadata,
  aiMetadata,
  isAnalyzing,
  imageSize,
  onSingleMode,
  onBatchMode,
  onAddImages,
  formatBytes,
  formatDimension,
}) => {
  const [isAiExpanded, setIsAiExpanded] = React.useState(true);
  const [copiedField, setCopiedField] = React.useState(null);

  const handleCopy = (field, text) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <section className="inspector-card">
      <div className="batch-mode-switcher" role="group" aria-label="Processing mode">
        <button
          type="button"
          className={`mode-pill ${!isBatchMode ? 'active' : ''}`}
          onClick={onSingleMode}
        >
          Single
        </button>
        <button
          type="button"
          className={`mode-pill ${isBatchMode ? 'active' : ''}`}
          onClick={onBatchMode}
        >
          Batch
        </button>
        {isBatchMode && (
          <button
            type="button"
            className="mode-pill mode-pill-action"
            onClick={onAddImages}
          >
            Add Images
          </button>
        )}
      </div>
      {selectedFile && !isBatchMode && (
        <div className="metadata-grid" style={{ gap: '6px' }}>
          <div className="metadata-row" style={{ marginBottom: '4px' }}>
            <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>File</span>
            <strong style={{ fontSize: '12px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={metadata?.name || selectedFile?.name || ''}>
              {metadata?.name || selectedFile?.name || '—'}
            </strong>
          </div>
          
          <div className="compact-metadata-grid">
            <div className="compact-metadata-item">
              <span className="compact-metadata-label">Dimensions</span>
              <span className="compact-metadata-value" title={`${formatDimension(imageSize.width)} × ${formatDimension(imageSize.height)}`}>
                {imageSize.width && imageSize.height
                  ? `${formatDimension(imageSize.width)} × ${formatDimension(imageSize.height)}`
                  : '—'}
              </span>
            </div>
            
            <div className="compact-metadata-item">
              <span className="compact-metadata-label">Size</span>
              <span className="compact-metadata-value">
                {metadata?.size != null
                  ? formatBytes(metadata.size)
                  : selectedFile?.size != null
                    ? formatBytes(selectedFile.size)
                    : '—'}
              </span>
            </div>

            <div className="compact-metadata-item">
              <span className="compact-metadata-label">Format</span>
              <span className="compact-metadata-value" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {(() => {
                  const raw = metadata?.type || selectedFile?.type || '';
                  const ext = metadata?.ext
                    || (selectedFile?.name?.split('.').pop()?.toUpperCase())
                    || (raw.split('/')[1]?.toUpperCase())
                    || '—';
                  return (
                    <>
                      <span style={{
                        background: 'rgba(107,196,171,0.15)',
                        color: '#6bc4ab',
                        border: '1px solid rgba(107,196,171,0.3)',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 700,
                        padding: '1px 5px',
                        letterSpacing: '0.04em',
                      }}>.{ext}</span>
                    </>
                  );
                })()}
              </span>
            </div>

            <div className="compact-metadata-item">
              <span className="compact-metadata-label">Color</span>
              <span className="compact-metadata-value">
                {metadata?.colorProfile || 'sRGB'}
              </span>
            </div>

            <div className="compact-metadata-item" style={{ gridColumn: '1 / -1' }}>
              <span className="compact-metadata-label">Modified</span>
              <span className="compact-metadata-value">
                {metadata?.lastModified
                  || (selectedFile?.lastModified
                    ? new Date(selectedFile.lastModified).toLocaleString()
                    : '—')}
              </span>
            </div>
          </div>


          {isAnalyzing && (
            <div className="ai-analysis-loading">
              <span className="spinner-small"></span>
              <span>AI Analyzing image content...</span>
            </div>
          )}

          {aiMetadata && (
            <div className="ai-metadata-section mt-3 pt-3 border-t border-white/10">
              <button 
                type="button"
                className="flex items-center justify-between w-full text-left mb-2 group hover:opacity-80 transition-opacity"
                onClick={() => setIsAiExpanded(!isAiExpanded)}
              >
                <div className="text-xs font-semibold text-blue-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm">auto_awesome</span>
                  AI INSIGHTS
                </div>
                <span className={`material-symbols-outlined text-sm text-white/40 transition-transform ${isAiExpanded ? 'rotate-180' : ''}`}>
                  expand_more
                </span>
              </button>
              
              {isAiExpanded && (
                <div className="ai-content-fade-in space-y-2">

                  <div className="metadata-row group/row">
                    <span>AI Title</span>
                    <div className="flex items-center gap-2 text-right">
                      <strong className="italic" title={aiMetadata.title}>"{aiMetadata.title}"</strong>
                      <button type="button" onClick={() => handleCopy('title', aiMetadata.title)} className="opacity-0 group-hover/row:opacity-100 transition-opacity text-white/50 hover:text-white flex-shrink-0" title="Copy">
                        <span className="material-symbols-outlined text-[14px]">{copiedField === 'title' ? 'check' : 'content_copy'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="metadata-row ai-row-stack group/row">
                    <div className="flex justify-between w-full items-center">
                      <span>ALT Text</span>
                      <button type="button" onClick={() => handleCopy('alt', aiMetadata.altText)} className="opacity-0 group-hover/row:opacity-100 transition-opacity text-white/50 hover:text-white" title="Copy">
                        <span className="material-symbols-outlined text-[14px]">{copiedField === 'alt' ? 'check' : 'content_copy'}</span>
                      </button>
                    </div>
                    <p className="ai-text-preview">{aiMetadata.altText}</p>
                  </div>
                  <div className="metadata-row ai-row-stack group/row">
                    <div className="flex justify-between w-full items-center">
                      <span>Social Caption</span>
                      <button type="button" onClick={() => handleCopy('social', aiMetadata.socialCaption)} className="opacity-0 group-hover/row:opacity-100 transition-opacity text-white/50 hover:text-white" title="Copy">
                        <span className="material-symbols-outlined text-[14px]">{copiedField === 'social' ? 'check' : 'content_copy'}</span>
                      </button>
                    </div>
                    <p className="ai-text-preview">{aiMetadata.socialCaption}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ImageFileInfoCard;
